/**
 * Remove one imported playlist from the signed-in user's My Music library.
 *
 * Deletes that UserPlaylist, its item occurrences, and its sync runs.
 * Does not delete user ProviderAssets, other playlists, listening history,
 * or any global catalog document. Does not call YouTube.
 */
import { collection, getDocs } from 'firebase/firestore';
import { userPlaylistId } from '../catalog/ids';
import { db } from '../firebase';
import { commitUserLibraryDeletes } from './userLibrary';

const suppressedPlaylistIds = new Set();

export class PlaylistRemovedError extends Error {
  constructor(playlistId) {
    super('Playlist was removed from My Music');
    this.name = 'PlaylistRemovedError';
    this.code = 'playlist-removed';
    this.playlistId = playlistId;
  }
}

function nonempty(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

export function playlistRemovalKeys(playlistOrId) {
  const raw = nonempty(
    typeof playlistOrId === 'string'
      ? playlistOrId
      : (playlistOrId?.playlistId || playlistOrId?.providerPlaylistId || playlistOrId?.userPlaylistId),
  );
  const explicit = nonempty(
    typeof playlistOrId === 'object' ? playlistOrId?.userPlaylistId : '',
  );
  const keys = new Set();
  const add = (value) => {
    const id = nonempty(value);
    if (!id) return;
    keys.add(id);
    if (id.startsWith('rhy_pl_')) return;
    if (id.startsWith('yt:')) {
      keys.add(id.slice(3));
      return;
    }
    keys.add(userPlaylistId('youtube', id));
  };
  add(raw);
  add(explicit);
  return [...keys];
}

export function suppressPlaylistRemoval(playlistOrId) {
  playlistRemovalKeys(playlistOrId).forEach((id) => suppressedPlaylistIds.add(id));
}

export function releasePlaylistRemoval(playlistOrId) {
  playlistRemovalKeys(playlistOrId).forEach((id) => suppressedPlaylistIds.delete(id));
}

export function isPlaylistRemovalSuppressed(playlistOrId) {
  return playlistRemovalKeys(playlistOrId).some((id) => suppressedPlaylistIds.has(id));
}

export function resetPlaylistRemovalStateForTests() {
  suppressedPlaylistIds.clear();
}

export function refuseRemovedPlaylistWrite(playlistOrId) {
  if (isPlaylistRemovalSuppressed(playlistOrId)) {
    const keys = playlistRemovalKeys(playlistOrId);
    throw new PlaylistRemovedError(keys[0] || '');
  }
}

async function listChildIds(firestore, getDocsFn, uid, playlistId, childCollection) {
  const snapshot = await getDocsFn(collection(
    firestore,
    'users',
    uid,
    'playlists',
    playlistId,
    childCollection,
  ));
  return (snapshot?.docs || []).map((entry) => entry.id);
}

export async function removePlaylistFromMyMusic(uid, playlistOrId, options = {}) {
  if (!nonempty(uid)) {
    throw new TypeError('uid must be a nonempty string');
  }
  const playlistIds = playlistRemovalKeys(playlistOrId);
  if (playlistIds.length === 0) {
    throw new TypeError('playlistId is required');
  }

  suppressPlaylistRemoval(playlistOrId);
  const firestore = options.firestore || db;
  const getDocsFn = options.getDocsFn || getDocs;
  const commitDeletes = options.commitUserLibraryDeletes || commitUserLibraryDeletes;

  try {
    const childDeletes = [];
    let itemDocsDeleted = 0;
    let syncRunDocsDeleted = 0;

    for (const playlistId of playlistIds) {
      // eslint-disable-next-line no-await-in-loop
      const itemIds = await listChildIds(firestore, getDocsFn, uid, playlistId, 'items');
      // eslint-disable-next-line no-await-in-loop
      const syncRunIds = await listChildIds(firestore, getDocsFn, uid, playlistId, 'syncRuns');
      itemIds.forEach((itemId) => {
        childDeletes.push({
          uid,
          segments: ['playlists', playlistId, 'items', itemId],
        });
      });
      syncRunIds.forEach((syncRunId) => {
        childDeletes.push({
          uid,
          segments: ['playlists', playlistId, 'syncRuns', syncRunId],
        });
      });
      itemDocsDeleted += itemIds.length;
      syncRunDocsDeleted += syncRunIds.length;
    }

    const parentDeletes = playlistIds.map((playlistId) => ({
      uid,
      segments: ['playlists', playlistId],
    }));

    const batchesUsed = (
      await commitDeletes(childDeletes, { firestore })
    ) + (
      await commitDeletes(parentDeletes, { firestore })
    );

    return {
      playlistIds,
      itemDocsDeleted,
      syncRunDocsDeleted,
      batchesUsed,
    };
  } catch (error) {
    releasePlaylistRemoval(playlistOrId);
    throw error;
  }
}
